'use client';

import React, { useEffect, useRef } from 'react';


interface Spark {
  x: number;y: number;
  vx: number;vy: number;
  life: number;maxLife: number;
  size: number;
}

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const spawnSparks = () => {
      const count = 3 + Math.floor(Math.random() * 4);
      const sx = canvas.width * (0.35 + Math.random() * 0.35);
      const sy = canvas.height * (0.35 + Math.random() * 0.3);
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1.5 + Math.random() * 3.5;
        sparksRef.current.push({
          x: sx, y: sy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 1.5,
          life: 0,
          maxLife: 30 + Math.floor(Math.random() * 40),
          size: 1 + Math.random() * 2.5
        });
      }
    };

    let spawnTimer = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      spawnTimer++;
      if (spawnTimer % 8 === 0) spawnSparks();

      sparksRef.current = sparksRef.current.filter((s) => s.life < s.maxLife);
      for (const s of sparksRef.current) {
        const t = s.life / s.maxLife;
        const alpha = (1 - t) * 0.9;
        const r = Math.floor(244 + (255 - 244) * t);
        const g = Math.floor(178 + (210 - 178) * t);
        const b = Math.floor(35 + (80 - 35) * t);

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size * (1 - t * 0.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fill();

        // Trailing line
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - s.vx * 4, s.y - s.vy * 4);
        ctx.strokeStyle = `rgba(${r},${g},${b},${alpha * 0.5})`;
        ctx.lineWidth = s.size * 0.5;
        ctx.stroke();

        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.08; // gravity
        s.life++;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section className="relative bg-ink pt-0">
      {/* Cinematic hero frame */}
      <div className="hero-cinematic">
        {/* Letterbox bars */}
        <div className="letterbox-bar letterbox-top" />
        <div className="letterbox-bar letterbox-bottom" />

        {/* Background image */}
        <img
          src="/assets/images/Cover_Page-1774717729459.jpg"
          alt="Cover page hero image"
          className="absolute inset-0 w-full h-full object-cover object-center z-[1]"
        />

        {/* Color grade overlay */}
        <div className="hero-image-overlay" />

        {/* Noise texture */}
        <div className="noise-overlay" />

        {/* Spark canvas */}
        <canvas ref={canvasRef} id="spark-canvas" className="absolute inset-0 w-full h-full z-[3] pointer-events-none" />

        {/* Overlay content */}
        <div className="absolute inset-0 z-10 flex flex-col justify-center items-center pb-14 px-8 sm:px-14 lg:px-20">
          {/* Company name */}
          <div className="mb-[4.5rem] w-full text-center">
            <span className="font-sans font-900 text-[clamp(2.1rem,5.25vw,4.5rem)] leading-none tracking-widest2 uppercase text-white">
              New Website coming soon
            </span>
          </div>

          {/* KPI line */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div style={{ backgroundColor: '#243746' }} className="px-6 py-3 rounded">
              <p className="font-mono text-[clamp(1.05rem,2.25vw,1.5rem)] font-700 tracking-widest uppercase text-center" style={{ color: '#BC3B4A', textShadow: '2px 2px 0px #808080' }}>
                while we restructure the site here is some basic information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-hero stats bar */}
      <div className="bg-charcoal-light border-b border-white/8">
        <div className="max-w-screen-xl mx-auto px-6 py-5 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {[
          { value: '3580', unit: 'Modules delivered', suffix: '' },
          { value: '8 facilities', unit: 'modDsys & partners', suffix: '' },
          { value: '70-120', unit: 'Modules per week', suffix: '' },
          { value: '7', unit: 'countries shipped to', suffix: '' }].
          map((stat, i) =>
          <div key={i} className="flex flex-col items-center">
              <span className="font-mono font-800 text-2xl leading-none" style={i === 0 ? { color: '#BC3B4A' } : i === 1 ? { color: '#BC3B4A' } : i === 2 ? { color: '#BC3B4A' } : i === 3 ? { color: '#BC3B4A' } : undefined}>
                {stat.value}{stat.suffix}
              </span>
              <span className="font-mono text-[0.58rem] font-600 tracking-widest uppercase mt-1" style={{ color: '#F4B223' }}>
                {stat.unit}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>);

}